import * as Clipboard from 'expo-clipboard';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TextInput, StyleSheet, FlatList } from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import Avatar from '@components/Avatar/Avatar';
import { EmailAvatar } from '@components/Avatar/EmailAvatar';
import { ContactType } from '@components/Invite/Contact';
import { ContactListV3 } from '@components/Invite/ContactListV3';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { CountBadgeWeb } from '@components/Modals/CountBadge.web';
import { NavItemRow } from '@components/Modals/NavItemRow.web';
import { WebModal } from '@components/Modals/WebModal.web';
import { Box, Text } from '@components/Restyle';
import Button from '@components/shared/Button/Button';
import { ListNavigationItemProps } from '@components/shared/ListNavigationItem/ListNavigationItem';
import { MarginRightM } from '@components/shared/ListSpacers';
import { SearchInputV3 } from '@components/shared/SearchInput/SearchInputV3';
import { TaskCreatedToast } from '@components/TaskList/TaskCreatedToast.web';
import {
  User,
  useCreateChatMutation,
  useCreateInviteMutation,
  useFriendRequestMutation,
  useListContactUsersQuery,
  ListChatsPaginationDocument,
  WebTutorialFlow,
  useCompleteWebTutorialFlowsMutation,
  GetMeDocument,
  SearchableResult,
  useSearchLazyQuery,
  useGetChatQuery,
  Group,
  useUpdateChatMutation,
  GetChatDocument,
  ListMessagesDocument,
  useCreateChatInvitationMutation,
} from '@graphql/generated';
import { useChatTutorialWeb } from '@hooks/useChatTutorialWeb';
import { useDebouncedSearch } from '@hooks/useDebouncedSearch';
import useMe from '@hooks/useMe';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { useUserPagination } from '@hooks/useUserPagination';
import { Search } from '@root/types';
import { MAX_GROUP_NAME_LENGTH } from '@schemas/createGroupSchema';
import theme from '@themes/theme';
import { convertUsersToContacts } from '@utils/convertUsersToContacts';
import { AsyncStorage } from '@utils/storage';
import { isGroupSearchResult } from '@utils/typeGuards';

interface ModalProps {
  showModal: boolean;
  onClose?: () => void;
  onOpenChat?: (data: any) => void;
  chatId?: string;
}

export const truncateText = (
  text: string | undefined,
  maxLength: number = 15
): string => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1) + '…';
};

export const NewChatModal: React.FC<ModalProps> = ({
  showModal,
  onClose,
  onOpenChat,
  chatId,
}) => {
  const { t } = useTranslation();
  const { t: s } = useTranslation('shared');
  // const [isGroupChat] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const { me } = useMe();
  const toast = useToast();
  const showToast = useCallback(
    (title?: string, isFailedToast?: boolean) => {
      toast.hideAll();

      let toastId: string | number | undefined;
      toastId = toast.show(
        <TaskCreatedToast
          isFailedToast={isFailedToast ?? false}
          title={title ?? 'Operation Completed'}
          duration={3000}
          onPress={() => {
            setTimeout(() => {
              if (toastId !== undefined) toast.hide(toastId);
            }, 0);
          }}
        />,
        {
          type: 'custom',
          duration: 3000,
          placement: 'bottom',
          animationType: 'slide-in',
          style: {
            marginBottom: 39,
            backgroundColor: 'transparent',
            shadowColor: 'transparent',
          },
          onClose: () => {},
        }
      );
    },
    [toast]
  );

  const { data: data1 } = useGetChatQuery({
    variables: { id: chatId },
    skip: !chatId,
  });

  const { getChat: chat } = data1 || { chat: undefined };

  const disabledId = chat?.users?.map((cu) => cu.id) ?? [];
  const alreadyInvitedUserIds =
    chat?.userRequests
      ?.map((ur) => ur.user?.id)
      .filter((id): id is string => !!id) ?? [];
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>([]);

  // const [setFriendRequestSuccess] = useState(false);

  const { data: listUsersData, loading: listUsersLoading } =
    useListContactUsersQuery({
      variables: { term: '' },
    });
  const { listContactUsers } = listUsersData || { listContactUsers: [] };
  // TA-10279: this query's `loading` was previously dropped entirely — the
  // browse-mode contact list rendered empty (no spinner, no skeleton) for
  // the whole first fetch. 150ms minimum hold per the global skeleton rule.
  const showContactsSkeleton = useMinimumVisible(
    listUsersLoading && listContactUsers.length === 0
  );

  const [globalSearch, { data: searchRtnData }] = useSearchLazyQuery();

  const searchData = useMemo(
    () => (searchRtnData?.search as Search<SearchableResult>[]) || [],
    [searchRtnData]
  );

  const [completeWebTutorialFlows] = useCompleteWebTutorialFlowsMutation({
    refetchQueries: [{ query: GetMeDocument }, 'getMe'],
  });

  const [friendRequest] = useFriendRequestMutation();

  const {
    isVisibleForChat,
    setIsVisibleForChat,
    setChatTutorialWebStateAndInStorage,
  } = useChatTutorialWeb();

  const [createChat, { data: createChatMutationData }] = useCreateChatMutation({
    refetchQueries: [
      { query: ListChatsPaginationDocument, variables: { first: 20 } },
    ],
  });

  useEffect(() => {
    if (!createChatMutationData) return;
    const { createChat: createdChat } = createChatMutationData;
    onOpenChat?.(createdChat);
  }, [createChatMutationData]);

  const [search, setSearch] = useState<string>('');
  const [groupUsers] = useState<ContactType[]>([]);
  const [groupStep, setGroupStep] = useState(1);
  const [groupDetaultName, setGroupDetaultName] = useState<string>('');
  const [groupInputName, setGroupInputName] = useState<string>('');

  // const ref_inputBox = useRef<TextInput>(null);

  const [isCreating, setIsCreating] = useState(false);
  const [showLinkCopied, setShowLinkCopied] = useState(false);

  const runGlobalSearch = useCallback(
    (term: string) => {
      globalSearch({
        variables: {
          term,
          size: 20,
          includeChats: false,
          includeMessages: false,
          includeProjects: false,
          includeDocuments: false,
          includeTasks: false,
          includeContacts: false,
          includeCompletedTask: false,
          includeTeams: false,
          includeGroups: true,
          includeUsers: false,
        },
      });
    },
    [globalSearch]
  );

  const { hasSearched, gatedTerm } = useDebouncedSearch(
    search,
    runGlobalSearch
  );

  // useUserPagination fires its own backend query as soon as searchTerm is
  // non-empty (it only skips on an empty string), so it needs the same
  // gate/debounce applied here rather than the raw live `search` value.
  // Reuses gatedTerm from the hook above instead of a second debounce
  // instance so both queries can't drift out of sync (TA-10166).
  const {
    users,
    loading: usersLoading,
    refreshing,
    hasNextPage,
    refresh,
    loadMoreUsers,
  } = useUserPagination({
    searchTerm: gatedTerm,
    fetchPolicy: 'cache-and-network',
  });

  // hasSearched is false below the min-char threshold and while a newer
  // term is still debouncing, so gating on it here stops Apollo's cached
  // last-response data from leaking in as stale group suggestions for the
  // current (shorter/cleared) term — TA-10166.
  const listGroup = useMemo(() => {
    if (!hasSearched) return [];
    return searchData
      .filter((t) => isGroupSearchResult(t))
      .map((t) => t.record as Group);
  }, [searchData, hasSearched]);

  const listUsersToContacts =
    convertUsersToContacts([...listContactUsers, { ...me }] as User[]) || [];

  const currentUserContact = listUsersToContacts.find((i) => i.id === me?.id);
  if (currentUserContact) {
    currentUserContact.displayName = s('myDisplayName', {
      displayName: me?.name,
    });
  }

  //Task tag user
  const listUsersToContactType = convertUsersToContacts(users ?? [])
    .filter((u) => !listUsersToContacts.some((c) => c.id === u.id))
    .map((lu) => {
      return { ...lu, isTaskTagUser: true } as ContactType;
    });

  const sortedListUsersToContacts = listUsersToContacts
    .filter((u) => !groupUsers.some((c) => c.id === u.id))
    .slice()
    .map((lu) => {
      return { ...lu, isTaskTagUser: false } as ContactType;
    })
    .sort((a, b) => {
      return a.firstName?.localeCompare(b.firstName);
    });

  useEffect(() => {
    let newname = '';
    const xlist = [
      ...sortedListUsersToContacts,
      ...listUsersToContactType,
    ].filter((t) => selectedContactIds.includes(t.id));

    const emails = selectedContactIds
      .filter((t) => t.includes('@'))
      .map((email) => email?.split('@')[0]);

    xlist.forEach((user) => {
      const { firstName } = user;
      if (newname != '') {
        newname += ',  ' + firstName;
      } else {
        newname += firstName;
      }
    });
    setGroupDetaultName(
      [emails.join(',  '), newname].filter(Boolean).join(',  ')
    );
  }, [selectedContactIds, sortedListUsersToContacts, listUsersToContactType]);

  const openChat = ({ firstName, lastName, id }: ContactType) => {
    const chatName = [firstName, lastName].filter((item) => item).join(' ');
    createChatFc(chatName, [id]);
  };

  const createGroupChat = () => {
    let chatName = '';
    const xlist = [
      ...sortedListUsersToContacts,
      ...listUsersToContactType,
    ].filter((t) => selectedContactIds.includes(t.id));

    if (xlist.length === 1) {
      const { firstName, lastName } = xlist[0];
      chatName = [firstName, lastName].filter((item) => !item).join(' ');
    } else {
      chatName = groupInputName;
    }

    createChatFc(chatName, selectedContactIds);
  };

  const createChatFc = (name: string, userIds: string[]) => {
    createChat({
      variables: {
        attributes: {
          name,
          userIds: userIds.filter((t) => !t.includes('@')),
          emails: userIds.filter((t) => t.includes('@')),
          groupChat: userIds.length > 1,
        },
      },
      // TA-10279: this call is shared by both the 1:1 path (openChat, which
      // never sets isCreating) and the group-creation path (createGroupChat,
      // which does) — previously had no onCompleted/onError at all, so a
      // failure on the group path left the Create Group button permanently
      // spinning and disabled with no error shown. Resetting isCreating
      // here is a no-op for the 1:1 path.
      onCompleted: () => {
        setIsCreating(false);
      },
      onError: () => {
        setIsCreating(false);
        showToast(t('shared:exceptionMsg'), true);
      },
    });
  };

  const [updateChat] = useUpdateChatMutation({
    refetchQueries: [
      { query: GetChatDocument, variables: { id: chatId } },
      { query: ListMessagesDocument, variables: { chatId } },
      'getChat',
    ],
  });

  const [createChatInvitation] = useCreateChatInvitationMutation();
  const hasClosedAddMembersModalRef = useRef(false);

  const closeAddMembersModalOnce = () => {
    if (hasClosedAddMembersModalRef.current) return;

    hasClosedAddMembersModalRef.current = true;
    setIsCreating(false);
    setTimeout(() => {
      onClose?.();
    }, 1);
  };

  const addMembersToGroup = () => {
    // TA-10279 (QA fix, PR self-review): this fires updateChat and
    // (conditionally) createChatInvitation with no lock at all — a
    // double-click could send duplicate invitation emails to the same
    // addresses, same bug class as the other three branches on this button.
    setIsCreating(true);
    hasClosedAddMembersModalRef.current = false;

    const existingUserIds = chat?.users?.map((u) => u.id) ?? [];

    if (!chat?.id || !existingUserIds) return;

    const newTaskTagUserEmails = listUsersToContactType
      .filter((t) => selectedContactIds.includes(t.id))
      .map((a) => a.email)
      .filter((email): email is string => !!email);
    const newUserIds = sortedListUsersToContacts
      .filter((t) => selectedContactIds.includes(t.id))
      .map((a) => a.id);
    const emailList = selectedContactIds.filter((t) => t.includes('@'));

    updateChat({
      variables: {
        id: chat.id,
        attributes: {
          userIds: [...existingUserIds, ...newUserIds],
        },
      },
      onCompleted: () => {
        if (!chat || !me) return false;
        closeAddMembersModalOnce();
      },
      onError: () => {
        showToast(t('shared:exceptionMsg'), true);
        closeAddMembersModalOnce();
      },
    });
    if (newTaskTagUserEmails.length > 0 || emailList.length > 0) {
      createChatInvitation({
        variables: {
          chatId: chat.id,
          emails: [...newTaskTagUserEmails, ...emailList],
        },
        onCompleted: () => {
          if (!chat || !me) return false;
          closeAddMembersModalOnce();
        },
        onError: () => {
          showToast(t('shared:exceptionMsg'), true);
          closeAddMembersModalOnce();
        },
      });
    }
  };

  const [createInviteMutation, { data: createInviteData }] =
    useCreateInviteMutation({
      variables: {},
    });

  useEffect(() => {
    if (!createInviteData?.createInvite) return;
    copyToClipboard(
      createInviteData.createInvite.replace(
        'register/register-with-email',
        'invited-user'
      )
    );
  }, [createInviteData]);

  const copyToClipboard = async (link: string) => {
    // toastRef?.current?.hideAll();
    // toastRef?.current?.show('Link Copied!', {
    //   duration: 2000,
    // });

    await Clipboard.setStringAsync(link || '');
    setShowLinkCopied(true);
    setTimeout(() => {
      setShowLinkCopied(false);
    }, 2000);
  };

  // const selectContact = (item: ContactType) => {
  //   if (!isGroupChat) {
  //     openChat(item);
  //   } else {
  //     if (item.email == me?.email) {
  //       return;
  //     }
  //     if (groupUsers?.find((u) => u.id === item.id)) {
  //       const selectedUsers = groupUsers.filter((u) => u.id !== item.id);
  //       setGroupUsers(selectedUsers);
  //     } else {
  //       setGroupUsers([...groupUsers, item]);
  //     }
  //     setSearch('');
  //     ref_inputBox.current?.focus();
  //   }
  // };

  const copyLinkItem: ListNavigationItemProps = {
    title: showLinkCopied
      ? t('shared:linkCopied')
      : t('shared:copyLinkToInvite'),
    iconName: 'Link',
    onPress: () => {
      if (showLinkCopied) return;
      if (chatId) {
        // from group chat waiting for BE
      } else {
        if (!me?.completedWebTutorialFlows?.includes('invite_member')) {
          completeWebTutorialFlows({
            variables: {
              flowType: WebTutorialFlow.InviteMember,
            },
          });
        }
        createInviteMutation();
      }
    },
  };
  const linkItems: ListNavigationItemProps[] = [copyLinkItem];

  const selectedUserList = [
    ...sortedListUsersToContacts,
    ...listUsersToContactType,
  ].filter((t) => selectedContactIds.includes(t.id));
  // const selectedMemberNames = selectedUserList
  //   .map((user) => {
  //     const fullName = [user.firstName, user.lastName]
  //       .filter(Boolean)
  //       .join(' ')
  //       .trim();
  //     return fullName || user.name || '';
  //   })
  //   .filter(Boolean)
  //   .slice(0, 4);
  // const selectedMembersLabel = selectedMemberNames.join(', ');
  // const truncatedChatName = truncateText(chat?.name ?? '', 50);
  // const addMembersConfirmMessageRaw = String(
  //   t('models:chat.addMembers.messageInterval', {
  //     postProcess: 'interval',
  //     count: selectedUserList.length,
  //     members: selectedMembersLabel,
  //     chatName: truncatedChatName,
  //     restCount: selectedUserList.length - 4,
  //   })
  // );
  // const addMembersConfirmMessageParts =
  //   selectedMembersLabel &&
  //   addMembersConfirmMessageRaw.includes(selectedMembersLabel)
  //     ? addMembersConfirmMessageRaw.split(selectedMembersLabel)
  //     : [];
  const isTasktagUser =
    selectedContactIds.length === 1 &&
    selectedUserList.length === 1 &&
    selectedUserList[0].isTaskTagUser;

  const buttonTitle = () => {
    let xtitle: string;
    if (chatId) {
      xtitle = t('models:chat.addToGroup');
      if (selectedContactIds.length === 1) {
        if (selectedUserList.length === 1) xtitle = t('models:chat.addToGroup');
        else {
          xtitle = t('models:chat.sendInvite');
        }
      }
    } else {
      xtitle = t('models:chat.startChat');
      if (selectedContactIds.length === 1) {
        if (selectedUserList.length === 1)
          xtitle = isTasktagUser
            ? t('models:chat.sendRequest')
            : t('models:chat.startChat');
        else {
          xtitle = t('models:chat.sendInvite');
        }
      } else if (selectedContactIds.length > 1) {
        xtitle = t('models:chat.newGroup');
      }
    }

    return xtitle;
  };

  const selectedMembersComponent = (lineNumbers: number = 1) => {
    const maxCount = 6;
    const renderItem = (xid) => {
      const list = [
        ...sortedListUsersToContacts,
        ...listUsersToContactType,
      ].filter((t) => t.id === xid);

      const item = list.length > 0 ? list[0] : undefined;

      if (item) {
        return (
          <Box width={64} alignItems='center' marginBottom='s' mr='s'>
            <Box height={60} pt='xxs'>
              <Avatar
                size='large'
                id={item.id}
                avatar={item.avatar}
                label={item.name}
                isMultiple={item?.type === 'GROUP'}
                onDismiss={() => {
                  setSelectedContactIds((pre) =>
                    pre.filter((preid) => preid !== xid)
                  );
                }}
              />
            </Box>

            <Text
              variant='metadata'
              marginTop='xs'
              color='black'
              style={{ maxWidth: 64 }}
              numberOfLines={1}>
              {item.firstName || item.name}
            </Text>
          </Box>
        );
      } else {
        // email avatar fallback
        return (
          <Box width={64} alignItems='center' marginBottom='s' mr='s'>
            <Box height={60}>
              <EmailAvatar
                width={56}
                onDismiss={() => {
                  setSelectedContactIds((pre) => pre.filter((t) => t !== xid));
                }}
              />
            </Box>

            <Text
              variant='metadata'
              color='black'
              marginTop='xs'
              style={{ maxWidth: 64 }}
              numberOfLines={1}>
              {xid}
            </Text>
          </Box>
        );
      }
    };
    return selectedContactIds.length > 0 ? (
      <Box mt={lineNumbers === 1 ? 'm' : 'none'}>
        <Box flexDirection='row' alignItems='center'>
          <Text variant='labelEmphasized' color='textPrimary' ml='m' mr='xxs'>
            {selectedContactIds?.length === 1
              ? t('models:chat.selectedMember')
              : t('models:chat.selectedMembers')}
          </Text>
          <CountBadgeWeb count={selectedContactIds?.length.toString()} />
        </Box>
        {lineNumbers === 1 && selectedUserList.length < 7 ? (
          <FlatList
            style={{
              marginTop: theme.spacing.m,
            }}
            horizontal
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
            data={selectedContactIds}
            ListHeaderComponent={MarginRightM}
            ListFooterComponent={MarginRightM}
            renderItem={({ item: xid }) => renderItem(xid)}
          />
        ) : (
          <Box
            height={
              selectedContactIds.length < 7 || lineNumbers === 1
                ? 96 + 28 + lineNumbers * 2
                : 96 * 2 + 28
            }
            style={{
              overflow: 'hidden',
            }}>
            <FlatList
              showsVerticalScrollIndicator={false}
              style={{
                marginVertical: theme.spacing.s,
              }}
              data={selectedContactIds}
              numColumns={maxCount} //
              keyExtractor={(item) => item}
              columnWrapperStyle={{
                paddingHorizontal: theme.spacing.m,
                justifyContent: 'flex-start',
              }}
              renderItem={({ item: xid }) => renderItem(xid)}
            />
          </Box>
        )}
      </Box>
    ) : (
      <Box marginTop='m' />
    );
  };

  // groupDetaultName defaults to '', not undefined; ?? would never show the placeholder
  const groupNamePlaceholder = groupDetaultName
    ? groupDetaultName
    : t('models:chat.labels.groupChatName'); // NOSONAR

  return (
    <>
      {groupStep === 1 && (
        <WebModal
          title={
            chatId
              ? t('models:chat.inviteOrAddMember')
              : t('models:chat.labels.newChat')
          }
          onClose={() => {
            setIsCreating(false);
            onClose?.();
          }}
          visible={showModal}
          width={504}
          height={701}>
          <Box flex={1}>
            {!chatId && (
              <Box mt='m'>
                {linkItems.map((item) => {
                  return (
                    <NavItemRow
                      title={item.title}
                      iconName={item.iconName}
                      onPress={item.onPress}
                    />
                  );
                })}
              </Box>
            )}

            <Box
              marginHorizontal='m'
              mt='m'
              flexDirection='row'
              alignItems='center'>
              <SearchInputV3
                autoFocus={true}
                showBackButton={false}
                placeholder={t('models:chat.addContactByEmailNameOrGroup')}
                showOverlay={true}
                onTextChanged={(t: string) => setSearch(t)}
                value={search}
              />
            </Box>
            {selectedMembersComponent()}
            <Box flex={1} mt='xxs'>
              {search.length === 0 ? (
                <Box flex={1}>
                  <Box flexDirection='row' alignItems='center'>
                    <Text
                      variant='labelEmphasized'
                      color='textPrimary'
                      ml='m'
                      mr='xxs'>
                      {t('models:chat.yourContact')}
                    </Text>
                    <CountBadgeWeb
                      count={sortedListUsersToContacts?.length.toString()}
                    />
                  </Box>

                  <Text
                    mt='xxs'
                    variant='webBodyHintSubline'
                    color='grey04'
                    mx='m'
                    mb='xxs'>
                    {t('models:chat.chooseContactFirstToCreateAGroup')}
                  </Text>
                  <ContactListV3
                    disabledUsersId={disabledId}
                    invitedList={
                      chat?.pendingInvitations?.map((pi) => pi.email) ?? []
                    }
                    // onPressAvatar={(item: ContactType) => {
                    //   openChat(item);
                    // }}
                    isEditing={true}
                    contacts={sortedListUsersToContacts}
                    alphabetize={false}
                    selected={selectedContactIds}
                    onPress={(item: ContactType) => {
                      // when group chat is ready
                      if (selectedContactIds.includes(item.id))
                        setSelectedContactIds(
                          selectedContactIds.filter((t) => t !== item.id)
                        );
                      else
                        setSelectedContactIds([...selectedContactIds, item.id]);
                    }}
                    showEmptyStateNoSearch
                    loading={showContactsSkeleton}
                    showLoadingSkeleton
                  />
                </Box>
              ) : (
                <ContactListV3
                  disabledUsersId={disabledId}
                  invitedList={
                    chat?.pendingInvitations?.map((pi) => pi.email) ?? []
                  }
                  alreadyInvitedUserIds={alreadyInvitedUserIds}
                  onPressGroup={(group: Group, isAdd: boolean) => {
                    // when group chat is ready
                    if (isAdd) {
                      setSelectedContactIds([
                        ...selectedContactIds.filter(
                          (t) => !group.users?.some((gu) => gu.id === t)
                        ),
                        ...(group.users ?? []).map((gu) => gu.id),
                      ]);
                    } else {
                      setSelectedContactIds(
                        selectedContactIds.filter(
                          (t) => !group.users?.some((gu) => gu.id === t)
                        )
                      );
                    }
                  }}
                  contacts={sortedListUsersToContacts}
                  taskTagUsers={listUsersToContactType}
                  groupList={listGroup ?? []}
                  filterVal={search}
                  onPressEmailAddress={(emailAddress) => {
                    if (selectedContactIds.includes(emailAddress))
                      setSelectedContactIds(
                        selectedContactIds.filter((t) => t !== emailAddress)
                      );
                    else
                      setSelectedContactIds([
                        ...selectedContactIds,
                        emailAddress,
                      ]);
                  }}
                  // onPressAvatar={(item: ContactType) => {
                  //   openChat(item);
                  // }}
                  isEditing={true}
                  onPress={
                    (item: ContactType) => {
                      if (selectedContactIds.includes(item.id))
                        setSelectedContactIds(
                          selectedContactIds.filter((t) => t !== item.id)
                        );
                      else
                        setSelectedContactIds([...selectedContactIds, item.id]);
                      // if (sortedListUsersToContacts.some((t) => t.id === item.id))
                      //   if (selectedContactIds.includes(item.id))
                      //     setSelectedContactIds(
                      //       selectedContactIds.filter((t) => t !== item.id)
                      //     );
                      //   else
                      //     setSelectedContactIds([...selectedContactIds, item.id]);
                      // else {
                      //   setSelectedTTUserIds([...selectedTTUserIds, item.id]);
                      // setFriendRequestSuccess(true);
                      // friendRequest({
                      //   onCompleted: () => {
                      //     setTimeout(() => {
                      //       setFriendRequestSuccess(false);
                      //     }, 500);
                      //   },
                      //   onError: () => {
                      //     setTimeout(() => {
                      //       setFriendRequestSuccess(false);
                      //     }, 500);
                      //   },
                      //   variables: {
                      //     attributes: {
                      //       userIds: [item.id],
                      //     },
                      //   },
                      // });
                    }

                    // replace when be is ready
                  }
                  selected={selectedContactIds}
                  showEmptyStateNoSearch
                  loading={usersLoading}
                  showLoadingSkeleton
                  refreshing={refreshing}
                  onRefresh={refresh}
                  hasNextPage={hasNextPage}
                  loadMore={loadMoreUsers}
                />
              )}
            </Box>

            {/*{isGroupChat && (*/}
            <Box px='m' py='m' flexDirection='row'>
              <Button
                disabled={selectedContactIds.length === 0 || isCreating}
                isLoading={isCreating}
                fullWidth={true}
                paddingHorizontal='m'
                onPress={() => {
                  if (chatId) {
                    addMembersToGroup();
                  } else {
                    if (selectedContactIds.length === 1) {
                      if (selectedContactIds[0].includes('@')) {
                        // TA-10279 (QA fix): this branch never set
                        // `isCreating` before firing and had no `onError` —
                        // a failure left the button clickable with no
                        // spinner/lock and no feedback, and repeated clicks
                        // could send duplicate email invitations.
                        setIsCreating(true);
                        createChat({
                          variables: {
                            attributes: {
                              name: selectedContactIds[0],
                              userIds: [],
                              emails: [selectedContactIds[0]],
                              groupChat: false,
                            },
                          },
                          onCompleted: () => {
                            setIsCreating(false);
                            showToast(t('models:chat.connectionRequestSend'));
                            onClose?.();
                          },
                          onError: () => {
                            setIsCreating(false);
                            showToast(t('shared:exceptionMsg'), true);
                          },
                        });
                      } else {
                        const selectedContact = [
                          ...sortedListUsersToContacts,
                          ...listUsersToContactType,
                        ].find((item) => item.id === selectedContactIds[0]);

                        if (selectedContact) {
                          if (selectedContact.isTaskTagUser) {
                            // TA-10279 (QA fix): this branch never touched
                            // `isCreating` at all and had no `onError` —
                            // a failure left the button clickable with no
                            // spinner/lock and no feedback, and repeated
                            // clicks could send duplicate friend requests.
                            setIsCreating(true);
                            friendRequest({
                              variables: {
                                attributes: {
                                  userIds: [selectedContact.id],
                                },
                              },
                              onCompleted: async (data) => {
                                setIsCreating(false);
                                // TA-9535: a friend request creates a chat
                                // that is not messageable until accepted, so
                                // the web chat tutorial cannot meaningfully
                                // complete from here. Close the tutorial on
                                // the friend-request branch (matches the
                                // native fix in src/screens/Chats/NewChat.tsx).
                                if (isVisibleForChat) {
                                  setIsVisibleForChat(false);
                                  await setChatTutorialWebStateAndInStorage(
                                    '9'
                                  );
                                  await AsyncStorage.setItem(
                                    'isChatTutorialWebCompleted',
                                    'true'
                                  );
                                }
                                showToast(
                                  t('models:chat.connectionRequestSend')
                                );
                                onOpenChat?.({ id: data.friendRequest.id });
                              },
                              onError: () => {
                                setIsCreating(false);
                                showToast(t('shared:exceptionMsg'), true);
                              },
                            });
                          } else {
                            // TA-10279 (QA fix): the 1:1 path never set
                            // `isCreating` before calling openChat, even
                            // though `createChatFc` already resets it on
                            // completion/error — so the button never showed
                            // a spinner or locked while a 1:1 chat was
                            // being created.
                            setIsCreating(true);
                            openChat(selectedContact);
                          }
                        }
                      }
                    } else {
                      setGroupStep(2);
                    }
                  }
                }}
                isSmall
                backgroundColor={
                  selectedContactIds.length === 0 ? 'grey02' : 'black'
                }>
                <Text
                  variant='buttonLabel'
                  color={selectedContactIds.length === 0 ? 'grey04' : 'white'}>
                  {buttonTitle()}
                </Text>
              </Button>
            </Box>
          </Box>
        </WebModal>
      )}
      {groupStep === 2 && (
        <WebModal
          title={t('models:chat.labels.newGroupChat')}
          onClose={() => {
            onClose?.();
          }}
          visible={showModal}
          width={504}
          height={selectedContactIds.length > 6 ? 487 : 386}>
          <Box flex={1}>
            <Box m='m'>
              <Text variant='labelSmall'>
                {t('models:chat.labels.groupChatName')}
              </Text>
              <TextInput
                accessibilityLabel='Group Name Input'
                autoFocus
                maxLength={MAX_GROUP_NAME_LENGTH}
                style={[
                  styles.textInput,
                  {
                    outlineStyle: 'none',
                    outlineWidth: 0,
                    boxShadow: 'none',
                  },
                ]}
                placeholderTextColor={theme.colors.textPrimary}
                placeholder={groupNamePlaceholder}
                onChangeText={setGroupInputName}
                value={groupInputName}
              />
            </Box>

            {selectedMembersComponent(0)}
            <Box mx='m' mb='m' flexDirection='row'>
              <Box flex={1} marginRight='m'>
                <Button
                  // TA-10279 (QA fix, PR self-review): locked while
                  // isCreating so a user can't back out mid-mutation, land
                  // on step 1 mid-flight, and then have onOpenChat pull them
                  // into a group they thought they'd cancelled.
                  disabled={isCreating}
                  fullWidth={true}
                  onPress={() => {
                    setGroupStep(1);
                  }}
                  // borderColor='textPrimary'
                  isSmall
                  borderWidth={1}
                  borderRadius='xs'
                  borderColor='black'
                  backgroundColor='white'>
                  <Text variant='buttonLabel' color='black'>
                    {t('shared:back')}
                  </Text>
                </Button>
              </Box>
              <Box flex={1}>
                <Button
                  // TA-10279 (QA fix, Jira comment 58928): `isLoading`
                  // deliberately keeps the button's resting color per
                  // Button's documented design (spinner-only, see
                  // Button.web.tsx) — QA expected a visual disabled/grey
                  // state too, so `isCreating` is folded into `disabled`
                  // here as well, scoped to this modal's two buttons only.
                  disabled={selectedContactIds.length === 0 || isCreating}
                  isLoading={isCreating}
                  fullWidth={true}
                  onPress={() => {
                    setIsCreating(true);
                    createGroupChat();
                  }}
                  // borderColor='textPrimary'
                  isSmall
                  backgroundColor={
                    selectedContactIds.length === 0 ? 'grey02' : 'black'
                  }>
                  <Text
                    variant='buttonLabel'
                    color={
                      selectedContactIds.length === 0 ? 'grey04' : 'white'
                    }>
                    {t('shared:createGroup')}
                  </Text>
                </Button>
              </Box>
            </Box>
          </Box>
        </WebModal>
      )}
      {showAddMember && (
        <ConfirmModal
          isAlert={true}
          showModal={showAddMember}
          onClose={() => setShowAddMember(false)}
          onPress={() => addMembersToGroup()}
          buttonText={'    ' + t('shared:add') + '    '}
          title={t('models:chat.addMembers.cta')}
          message={t('models:chat.addMembers.messageInterval', {
            postProcess: 'interval',
            count:
              (selectedUserList?.length ?? 0) +
              ((selectedContactIds ?? []).filter((t) => t.includes('@'))
                .length ?? 0),
            members: [
              ...(selectedUserList?.map((u) => u.firstName)?.slice(0, 4) ?? []),
              ...(selectedContactIds ?? []).filter((t) => t.includes('@')),
            ],
            chatName: truncateText(chat?.name ?? '', 50),
            restCount:
              (selectedUserList?.length ?? 0) +
              ((selectedContactIds ?? []).filter((t) => t.includes('@'))
                .length ?? 0) -
              4,
          })}
        />
      )}
    </>
  );
};

const styles = StyleSheet.create({
  textInput: {
    height: 48,
    fontFamily: 'Inter_400Regular',
    backgroundColor: theme.colors.white,
    fontWeight: '400',
    borderRadius: 8,
    fontSize: 14,
    lineHeight: 16,
    padding: theme.spacing.m,
    borderWidth: 1,
    borderColor: theme.colors.black,
    marginTop: theme.spacing.xs,
  },
});
